import React, { useEffect, useState } from "react";
import { PrimaryButton, SoftButton } from "../../UI/DesignStandards";
import EventFeeAmountFields from "./EventFeeAmountFields";
import {
  EVENT_FEES_LABEL,
  eventHasFees,
  filledEventFees,
  formatEventFeesSummary,
} from "../../../utility/eventFees";

const formatEventNames = (events) => {
  const names = (events || []).map((event) => event.eventName).filter(Boolean);
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
};

const EventGroupSection = ({
  title,
  description,
  events,
  catalog,
  amounts,
  onAmountChange,
  onApply,
  onCancelEdit,
  saving = false,
  canManage = false,
  disabled = false,
  showGroupFees = true,
  children,
}) => {
  const needsFees = (events || []).every((event) => !eventHasFees(event));
  const [editing, setEditing] = useState(needsFees);
  const names = formatEventNames(events);
  const hasTypedFees = filledEventFees(amounts).length > 0;
  const savedSummary = formatEventFeesSummary(
    (events || []).find(eventHasFees)?.fees
  );

  useEffect(() => {
    if (needsFees) setEditing(true);
  }, [needsFees]);

  if (!events || events.length === 0) return null;

  const saveLabel =
    events.length === 1 ? `Save fees for ${events[0].eventName}` : "Save fees";

  const handleSave = async () => {
    try {
      await onApply();
      setEditing(false);
    } catch {
      // Error is shown on the page.
    }
  };

  const handleCancel = () => {
    setEditing(false);
    if (onCancelEdit) onCancelEdit();
  };

  return (
    <section className="mb-8">
      <h3 className="heading-display text-lg mb-3">{title}</h3>
      {description ? (
        <p className="text-sm text-brand-muted mb-3">{description}</p>
      ) : null}
      {canManage && showGroupFees ? (
        needsFees || editing ? (
          <div
            className={`p-4 mb-4 rounded-lg ${
              needsFees
                ? "border border-brand-gold-ink/40 bg-brand-cream/60"
                : "card-inset"
            }`}
          >
            <h4 className="heading-display text-lg mb-1">
              {needsFees ? "Set fees (per bowler)" : "Edit fees (per bowler)"}
            </h4>
            <p className="text-sm text-brand-muted mb-4">
              {needsFees
                ? `No fees on ${names} yet.`
                : `Save to update ${names}.`}
            </p>
            <EventFeeAmountFields
              title=""
              fees={catalog}
              amounts={amounts}
              onAmountChange={onAmountChange}
              disabled={disabled}
              events={events}
            />
            <div className="flex justify-end gap-2 mt-4">
              {!needsFees ? (
                <SoftButton color="cream" onClick={handleCancel} disabled={saving}>
                  Cancel
                </SoftButton>
              ) : null}
              <PrimaryButton
                type="button"
                onClick={handleSave}
                disabled={disabled || (needsFees && !hasTypedFees)}
                loading={saving}
                className="w-full sm:w-auto"
              >
                {saveLabel}
              </PrimaryButton>
            </div>
          </div>
        ) : (
          <div className="card-inset p-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h4 className="text-sm font-display font-bold text-brand-charcoal">
                {EVENT_FEES_LABEL}
              </h4>
              <p className="text-sm text-brand-muted">
                {savedSummary || "None yet"}
              </p>
            </div>
            <SoftButton
              color="cream"
              onClick={() => setEditing(true)}
              disabled={disabled}
            >
              Edit fees
            </SoftButton>
          </div>
        )
      ) : null}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">{children}</div>
    </section>
  );
};

export default EventGroupSection;
